import { z } from 'zod';
import { wireUnion } from '@/lib/api/parse-response';
import type {
  TaxonomyFieldTemplate,
  TaxonomyFieldTemplateChangeLogEntry,
  TaxonomyFieldTemplateListRef,
} from '../model/types';

const listRefSchema: z.ZodType<TaxonomyFieldTemplateListRef> = z.looseObject({
  listId: z.string(),
  listVersionId: z.string().nullable().optional(),
  valueColumnId: z.string(),
  codeColumnId: z.string().nullable().optional(),
});

export const fieldTemplateSchema: z.ZodType<TaxonomyFieldTemplate> = z.looseObject({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  category: z.string(),
  group: wireUnion<TaxonomyFieldTemplate['group']>(),
  type: wireUnion<TaxonomyFieldTemplate['type']>(),
  level: z.string().nullable(),
  required: z.boolean(),
  visible: z.boolean(),
  unique: z.boolean(),
  format: z.string().nullable(),
  defaultValue: z.string().nullable(),
  validationPattern: z.string().nullable(),
  transform: wireUnion<NonNullable<TaxonomyFieldTemplate['transform']>>().nullable(),
  listRef: listRefSchema.nullable(),
  values: z.array(z.looseObject({ value: z.string(), code: z.string() })),
  orgWide: z.boolean(),
  libraryKey: z.string().nullable(),
  customized: z.boolean(),
  updatedBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const fieldTemplateListSchema = z.array(fieldTemplateSchema);

export const fieldTemplateChangeLogSchema: z.ZodType<TaxonomyFieldTemplateChangeLogEntry[]> =
  z.array(
    z.looseObject({
      id: z.string(),
      templateId: z.string(),
      actorId: z.string(),
      action: wireUnion<TaxonomyFieldTemplateChangeLogEntry['action']>(),
      reason: z.string().nullable(),
      before: z.record(z.string(), z.unknown()).nullable(),
      after: z.record(z.string(), z.unknown()).nullable(),
      createdAt: z.string(),
    }),
  );

// `removedIds` is absent from an API that predates it; `deletedIds` is the closest it has.
export const bulkDeleteFieldTemplatesSchema = z
  .looseObject({ deletedIds: z.array(z.string()), removedIds: z.array(z.string()).optional() })
  .transform((result) => result.removedIds ?? result.deletedIds);

export const restoreFieldTemplatesSchema = z.looseObject({
  restoredIds: z.array(z.string()),
  blocked: z.array(z.looseObject({ id: z.string(), name: z.string(), code: z.string() })),
});
export type RestoreFieldTemplatesResult = z.infer<typeof restoreFieldTemplatesSchema>;
